import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Calendar, RefreshCw, Link2, Unlink, CheckCircle2 } from "lucide-react";
import { SyncStatusStrip } from "./SyncStatusStrip";
import { localeFor, errMsg } from "./calendarUtils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// `status` + `onRefresh` sont optionnels : un parent qui a déjà besoin du statut de
// connexion (ex. BookingCalendar pour gater la création de créneaux) peut le fournir
// et reprendre la main sur le refetch. Sans ces props, le composant se gère seul
// (comportement historique — cas de AvailabilityAdmin, qui le réutilise tel quel).
// `mismatchCount` + `onShowMismatches` sont transmis tels quels à SyncStatusStrip
// (Phase 7) : un parent qui a déjà les bookings en mémoire (BookingCalendar) peut
// fournir un callback pour ouvrir la première réservation en écart ; sans ça, la
// bandelette se contente du compteur agrégé du statut de connexion.
export const GoogleCalendarCard = ({ onSynced, status: statusProp, onRefresh, mismatchCount, onShowMismatches }) => {
  const t = useT("admin");
  const { lang } = useLanguage();
  const controlled = statusProp !== undefined;
  const [ownStatus, setOwnStatus] = useState(null);
  const [loading, setLoading] = useState(!controlled);
  const [syncing, setSyncing] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const status = controlled ? statusProp : ownStatus;

  const fetchOwnStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/calendar/connection");
      setOwnStatus(data);
    } catch (e) {
      setOwnStatus({ configured: false, connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    if (controlled) {
      onRefresh && onRefresh();
    } else {
      fetchOwnStatus();
    }
  }, [controlled, onRefresh, fetchOwnStatus]);

  useEffect(() => {
    if (!controlled) fetchOwnStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gère le retour OAuth (?gcal=connected|error|scope_missing)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gcal");
    if (g === "connected") {
      toast.success(t("gcal.connectedToast"));
      refresh();
    } else if (g === "scope_missing") {
      // Consentement accordé SANS la case Agenda : la connexion serait « réussie »
      // mais toute écriture échouerait ensuite en 403, sans rien pour le signaler.
      // On refuse en amont et on dit quoi refaire.
      toast.error(t("gcal.scopeMissing"));
    } else if (g === "error") {
      toast.error(t("gcal.errorToast"));
    }
    if (g) {
      params.delete("gcal");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const connect = async () => {
    try {
      // A POST: it writes the flow's state, which names an operator who started it.
      const { data } = await api.post("/oauth/google-calendar/start");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(errMsg(e, t, "gcal.errorToast"));
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/tenant/calendar/connection");
      toast.success(t("gcal.disconnected"));
      refresh();
    } catch (e) {
      toast.error(errMsg(e, t, "gcal.errorToast"));
    } finally {
      setConfirmingDisconnect(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/tenant/calendar/sync");
      if (data.failed > 0) {
        // Un run partiellement/totalement raté n'est PAS un succès — le praticien
        // doit savoir que son agenda Google est incomplet.
        toast.warning(t("gcal.syncPartial", { pushed: data.pushed, failed: data.failed }));
      } else {
        toast.success(t("gcal.syncDone", { pushed: data.pushed, busy: data.busy_count }));
      }
      refresh();
      onSynced && onSynced();
    } catch (e) {
      toast.error(errMsg(e, t, "gcal.errorToast"));
    } finally {
      setSyncing(false);
    }
  };

  if (controlled ? status == null : loading) return null;

  const connected = status?.connected;

  return (
    <>
    <div
      data-testid="gcal-card"
      className="bg-white border border-eh-border-soft rounded-2xl px-4 py-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`p-2 rounded-xl ${connected ? "bg-eh-sage/20" : "bg-eh-cream"}`}>
          <Calendar className={`w-5 h-5 ${connected ? "text-eh-sage-deep" : "text-eh-muted"}`} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-eh-ink text-sm">{t("gcal.title")}</span>
            {connected && (
              <span className="inline-flex items-center gap-1 text-[11px] text-eh-sage-deep bg-eh-sage/15 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="w-3 h-3" /> {t("gcal.connected")}
              </span>
            )}
          </div>
          {connected ? (
            <p className="text-xs text-eh-muted truncate" data-testid="gcal-status-email">
              {t("gcal.connectedAs")} {status.email} · {t("gcal.lastSync")}:{" "}
              {status.last_sync ? new Date(status.last_sync).toLocaleString(localeFor(lang)) : t("gcal.never")}
            </p>
          ) : (
            <p className="text-xs text-eh-muted">
              {status?.configured === false ? t("gcal.notConfigured") : t("gcal.desc")}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {connected ? (
          <>
            <button
              onClick={sync}
              disabled={syncing}
              data-testid="gcal-sync-btn"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-eh-ink text-eh-bg hover:opacity-90 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? t("gcal.syncing") : t("gcal.syncNow")}
            </button>
            <button
              onClick={() => setConfirmingDisconnect(true)}
              data-testid="gcal-disconnect-btn"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-eh-border-soft hover:bg-eh-cream text-eh-muted"
            >
              <Unlink className="w-4 h-4" /> {t("gcal.disconnect")}
            </button>
          </>
        ) : (
          <button
            onClick={connect}
            disabled={status?.configured === false}
            data-testid="gcal-connect-btn"
            className="inline-flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-full bg-sol-gold text-eh-ink hover:opacity-90 disabled:opacity-50"
          >
            <Link2 className="w-4 h-4" /> {t("gcal.connect")}
          </button>
        )}
      </div>
      </div>

      {connected && (
        <SyncStatusStrip
          status={status}
          mismatchCount={mismatchCount}
          onShowMismatches={onShowMismatches}
          onRefresh={refresh}
        />
      )}
    </div>

    <AlertDialog open={confirmingDisconnect} onOpenChange={setConfirmingDisconnect}>
      <AlertDialogContent data-testid="gcal-disconnect-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("gcal.disconnectWarnTitle")}</AlertDialogTitle>
          <AlertDialogDescription data-testid="gcal-disconnect-confirm-body">
            {status?.future_synced_bookings > 0
              ? t("gcal.disconnectWarnBody", { n: status.future_synced_bookings })
              : t("gcal.disconnectWarnBodyNone")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="gcal-disconnect-cancel">{t("cal.confirmDetachCancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={disconnect} data-testid="gcal-disconnect-confirm-btn">{t("gcal.disconnect")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
